import { useEffect, useState } from "react";
import { listPublicEvents } from "../lib/api";
import type { Event } from "../lib/types";

interface LandingPageProps {
  navigate: (path: string) => void;
  initialEvents?: Event[];
}

export type LandingDisplayState = "loading" | "error" | "empty" | "ready";

export function getLandingDisplayState(isLoading: boolean, hasError: boolean, eventCount: number): LandingDisplayState {
  if (isLoading) return "loading";
  if (hasError) return "error";
  return eventCount === 0 ? "empty" : "ready";
}

export function selectFeaturedEvent(events: Event[], now = Date.now()): Event | undefined {
  return events
    .filter((event) => event.visibility === "public" && event.status === "live" && new Date(event.startsAt).getTime() >= now)
    .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime())[0];
}

export function buildTicketSearchPath(query: string, dateFilter: string, locationFilter: string): string {
  const params = new URLSearchParams();
  if (query.trim()) params.set("q", query.trim());
  if (dateFilter !== "all") params.set("date", dateFilter);
  if (locationFilter !== "all") params.set("location", locationFilter);
  return `/tickets${params.toString() ? `?${params.toString()}` : ""}`;
}

function formatDate(value?: string) {
  if (!value) return "Date TBA";
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", weekday: "short" });
}

function formatPrice(event?: Event) {
  const active = event?.ticketTiers?.filter((tier) => tier.status === "active") ?? [];
  if (active.length === 0) return "Details";
  return `$${(Math.min(...active.map((tier) => tier.priceCents)) / 100).toFixed(2)}`;
}

function LandingEventArtwork({ event, featured = false }: { event: Pick<Event, "title" | "flyerImageUrl">; featured?: boolean }) {
  const [hasFailed, setHasFailed] = useState(false);
  const artworkClass = featured ? "featured-event-card__artwork" : "landing-event-card__artwork";

  if (!event.flyerImageUrl || hasFailed) {
    return (
      <div className={`${artworkClass} ${artworkClass}--fallback`} aria-label={`${event.title} artwork unavailable`}>
        <img src="/antix-logo.png" alt="" />
      </div>
    );
  }

  return (
    <img
      className={artworkClass}
      src={event.flyerImageUrl}
      alt=""
      loading={featured ? "eager" : "lazy"}
      onError={() => setHasFailed(true)}
    />
  );
}

export default function LandingPage({ navigate, initialEvents }: LandingPageProps) {
  const [events, setEvents] = useState<Event[]>(initialEvents ?? []);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(!initialEvents);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (initialEvents) return;
    setIsLoading(true);
    setHasError(false);
    listPublicEvents()
      .then((items) => setEvents(items.slice(0, 4)))
      .catch(() => {
        setEvents([]);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [initialEvents]);

  const featuredEvent = selectFeaturedEvent(events);
  const displayState = getLandingDisplayState(isLoading, hasError, events.length);
  const locations = Array.from(new Set(events.map((event) => event.city || event.venueName).filter(Boolean))).slice(0, 6);

  function searchTickets() {
    navigate(buildTicketSearchPath(query, dateFilter, locationFilter));
  }

  return (
    <section className="page landing-page">
      <div className="landing-hero-grid">
        <div className="hero-copy">
          <div className="eyebrow">Next up</div>
          <h1>
            The night starts <span className="hero-underlined-word">here.</span>
          </h1>
          <p>Discover the hottest events. Get your tickets fast and skip the line.</p>
          <div className="hero-actions">
            <button className="primary-action" onClick={() => navigate("/tickets")}>
              Get Tickets Now
            </button>
          </div>
        </div>
        <article className="featured-event-card" aria-label="Featured event">
          {featuredEvent ? (
            <>
              <div className="featured-event-card__media">
                <LandingEventArtwork key={featuredEvent.id} event={featuredEvent} featured />
                <span className="featured-event-card__date">{formatDate(featuredEvent.startsAt)}</span>
                <div className="featured-event-card__copy">
                  <h2>{featuredEvent.title}</h2>
                  <p>{[featuredEvent.venueName, featuredEvent.city, featuredEvent.region].filter(Boolean).join(" - ")}</p>
                  {featuredEvent.ageRequirement && <span>{featuredEvent.ageRequirement}</span>}
                </div>
              </div>
              <div className="featured-event-card__footer">
                <span>{featuredEvent.ticketTiers?.[0]?.name ?? "Tickets"}</span>
                <strong>{formatPrice(featuredEvent)}</strong>
                <button className="primary-action" type="button" onClick={() => navigate(`/e/${featuredEvent.slug}`)}>
                  Buy Tickets
                </button>
              </div>
            </>
          ) : displayState === "loading" ? (
            <div className="featured-event-card__empty">
              <img src="/antix-logo.png" alt="AnTix logo" />
              <h2>Loading events...</h2>
              <p>We’re finding the next event for you.</p>
            </div>
          ) : displayState === "error" ? (
            <div className="featured-event-card__empty">
              <img src="/antix-logo.png" alt="AnTix logo" />
              <h2>We couldn't load events right now</h2>
              <p>Please try again in a moment.</p>
              <button className="primary-action" type="button" onClick={() => navigate("/tickets")}>
                Browse Tickets
              </button>
            </div>
          ) : (
            <div className="featured-event-card__empty">
              <img src="/antix-logo.png" alt="AnTix logo" />
              <h2>No events announced yet</h2>
              <p>Check back soon for the next AnTix event.</p>
              <button className="primary-action" type="button" onClick={() => navigate("/tickets")}>
                Browse Tickets
              </button>
            </div>
          )}
        </article>
      </div>

      <form className="landing-search" aria-label="Find tickets" onSubmit={(event) => {
        event.preventDefault();
        searchTickets();
      }}>
        <label className="landing-search__field">
          <span>Search</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search events, artists, or venues..." />
        </label>
        <label className="landing-search__select">
          <span>Date</span>
          <select value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
          </select>
        </label>
        <label className="landing-search__select">
          <span>Location</span>
          <select value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)}>
            <option value="all">All Locations</option>
            {locations.map((location) => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </label>
        <button className="primary-action landing-search__button" type="submit" aria-label="Search tickets">
          Search
        </button>
      </form>

      <section className="feature-strip" aria-label="AnTix features">
        <article className="feature-strip__item feature-strip__item-ticket">
          <i aria-hidden="true" />
          <span>Fast Checkout</span>
          <p>Secure payments. Tickets in minutes.</p>
        </article>
        <article className="feature-strip__item feature-strip__item-qr">
          <i aria-hidden="true" />
          <span>Mobile Tickets</span>
          <p>QR-ready entry from your phone.</p>
        </article>
        <article className="feature-strip__item feature-strip__item-shield">
          <i aria-hidden="true" />
          <span>Secure Payments</span>
          <p>Safe and reliable checkout.</p>
        </article>
        <article className="feature-strip__item feature-strip__item-support">
          <i aria-hidden="true" />
          <span>Event Updates</span>
          <p>Get the details you need before you go.</p>
        </article>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <span>Upcoming</span>
          <h2>Your next night out starts here</h2>
        </div>
        <div className="event-card-grid">
          {events.length === 0 && (
            <article className="event-card landing-event-card">
              <span className="event-date">Upcoming</span>
              <strong>{displayState === "error" ? "We couldn't load events right now" : displayState === "loading" ? "Loading events..." : "No upcoming events"}</strong>
              <small>{displayState === "error" ? "Please try again in a moment." : displayState === "loading" ? "We’re checking for the next event." : "New events will appear here as soon as tickets go live."}</small>
            </article>
          )}
          {events.map((event) => (
            <button className="event-card landing-event-card" key={event.id} onClick={() => navigate(`/e/${event.slug}`)}>
              <LandingEventArtwork event={event} />
              <span className="event-date">{new Date(event.startsAt).toLocaleDateString()}</span>
              <strong>{event.title}</strong>
              <small>{event.venueName}</small>
              <span className="card-link">View event</span>
            </button>
          ))}
        </div>
      </section>
    </section>
  );
}
